import { useCallback, useMemo, useState } from 'react';
import useSWR, { trigger } from 'swr';
import { destroyChildJournal } from 'child-journals/api';

import {
  postChildJournal,
  patchJournalEntitlement,
  bulkPatchJournalEntitlements,
} from './api';

const UNASSIGNED_BOOKS_KEY = '/v3/journal_entitlements?unassigned=true&kind=book';
const UNASSIGNED_ADD_ONS_KEY = '/v3/journal_entitlements?unassigned=true&kind=add_on';
const MY_CHILD_JOURNALS_KEY = '/v3/child_journals?section=assigned';
const MY_CHILD_JOURNALS = '/v3/child_journals';
const UNASSIGNED_CHILD_JOURNALS_KEY = '/v3/child_journals?section=unassigned';
const GIFT_CERTIFICATES_KEY = '/v3/commerce/gift_certificates';

// A journal created up-front for an unassigned book carries this placeholder name
// until the customer names it in the set-up flow. Placeholders are what let the
// dashboard reuse the same journal id across refreshes instead of creating a new
// one every visit.
export const PLACEHOLDER_NAME = 'Baby';

// Attribute casing for these newer v3 resources isn't consistent, so read across
// the plausible variants rather than assuming kebab-case.
const attr = (resource, key) => {
  const attributes = resource?.attributes || {};
  const kebab = key.replace(/_/g, '-');
  const snake = key.replace(/-/g, '_');
  if (attributes[key] !== undefined) return attributes[key];
  if (attributes[kebab] !== undefined) return attributes[kebab];
  if (attributes[snake] !== undefined) return attributes[snake];
  return undefined;
};

const relationships = (resource, key) => {
  const attributes = resource?.relationships || {};
  const kebab = key.replace(/_/g, '-');
  const snake = key.replace(/-/g, '_');
  if (attributes[key] !== undefined) return attributes[key];
  if (attributes[kebab] !== undefined) return attributes[kebab];
  if (attributes[snake] !== undefined) return attributes[snake];
  return undefined;
};

const resourcesFromResponse = response => response?.data?.data || [];

const resourcesFromResponseIncluded = response => response?.data || [];

export const entitlementToPlain = resource => ({
  id: resource.id,
  kind: attr(resource, 'kind'),
  description: attr(resource, 'description'),
  sku: attr(resource, 'sku'),
  allocationGroup: attr(resource, 'allocation-group'),
  childJournalId: attr(resource, 'child-journal-id'),
  bundleName: attr(resource, 'bundle-name'),
  entitlements: attr(resource, 'entitlements') || {},
  raw: resource,
  title: attr(resource, 'product')?.title,
});

// `entitlements` here is a capability summary OBJECT, not a list — see ./entitlements.js.
export const journalToPlain = resource => ({
  id: resource.id,
  firstName: attr(resource, 'first-name') || PLACEHOLDER_NAME,
  entitlements: attr(resource, 'entitlements') || {},
  createdAt: attr(resource, 'created-at') || {},
  subjectId: attr(resource, 'subject-id') || {},
  storyCount: attr(resource, 'story-count'),
  // unassigned → assigned → initialized
  state: attr(resource, 'state'),
  title: (attr(resource, 'entitlements') || {})?.['plan-name'] || 'À La Carte Book',
  gifted: attr(resource, 'gifted'),
  giftCertificateId: relationships(resource, 'gift-certificate')?.data?.id,
  raw: resource,
});

const giftCertificateToPlain = resource => ({
  id: resource.id,
  recipientName: attr(resource, 'name-of-recipient'),
  giverName: attr(resource, 'name-of-giver'),
  styleId: resource?.relationships?.['gift-certificate-style']?.data?.id,
  couponCode: attr(resource, 'coupon-code'),
  state: attr(resource, 'state'),
  paid: attr(resource, 'paid'),
  claimerId: attr(resource, 'claimer-id'),
  memberId: attr(resource, 'member-id'),
  childJournalId: resource?.relationships?.['child-journal']?.data?.id,
  entitlements: resource?.relationships?.['child-journal']?.data?.entitlements || {},
  raw: resource,
});

export const useUnassignedBookEntitlements = () => {
  const { data, error, isValidating } = useSWR(UNASSIGNED_BOOKS_KEY);
  const entitlements = useMemo(
    () => resourcesFromResponse(data).map(entitlementToPlain),
    [data]
  );
  return { entitlements, isLoading: !data && !error, isValidating, error };
};

export const useUnassignedAddOnEntitlements = () => {
  const { data, error, isValidating } = useSWR(UNASSIGNED_ADD_ONS_KEY);
  const entitlements = useMemo(
    () => resourcesFromResponse(data).map(entitlementToPlain),
    [data]
  );
  return { entitlements, isLoading: !data && !error, isValidating, error };
};

export const useMyChildJournals = () => {
  const { data, error, isValidating } = useSWR(MY_CHILD_JOURNALS_KEY);
  const journals = useMemo(() => resourcesFromResponse(data).map(journalToPlain), [data]);
  return { journals, isLoading: !data && !error, isValidating, error };
};

export const useMyAllChildJournals = () => {
  const { data, error, isValidating, revalidate } = useSWR(MY_CHILD_JOURNALS);
  const journals = useMemo(() => resourcesFromResponse(data).map(journalToPlain), [data]);
  return { journals, isLoading: !data && !error, isValidating, error, revalidate };
};

// A single journal by id. `enabled: false` skips the request, so callers that
// only need it conditionally (e.g. the redemption flow prefilling the baby's
// name) don't fire it for everyone.
export const useChildJournal = (journalId, { enabled = true } = {}) => {
  const key = enabled && journalId ? `/v3/child_journals/${journalId}` : null;
  const { data, error, isValidating } = useSWR(key);
  const journal = useMemo(() => {
    const resource = data?.data?.data;
    return resource ? journalToPlain(resource) : null;
  }, [data]);
  return { journal, isLoading: !!key && !data && !error, isValidating, error };
};

// Journals that exist but aren't assigned to a book yet. These share the
// Unassigned tab with the unassigned book entitlements.
export const useUnassignedChildJournals = () => {
  const { data, error, isValidating } = useSWR(UNASSIGNED_CHILD_JOURNALS_KEY);
  const journals = useMemo(() => resourcesFromResponse(data).map(journalToPlain), [data]);
  return { journals: journals.filter(journal => journal.state === 'unassigned'), isLoading: !data && !error, isValidating, error };
};

// `enabled: false` skips the request (SWR treats a null key as "don't fetch"),
// so callers that only need this list conditionally — e.g. the gift wizard, which
// only looks a certificate up when editing one — don't fire it for everyone.
export const useGiftCertificates = ({ enabled = true } = {}) => {
  const { data, error, isValidating } = useSWR(enabled ? GIFT_CERTIFICATES_KEY : null);
  let response = resourcesFromResponseIncluded(data) || {};
  const included = response.included || [];

  response?.data?.forEach(item => {
    const childJournal = item.relationships?.["child-journal"];
    const childJournalEntitlements = item.relationships?.["journal-entitlements"];
    if (!childJournal?.data && !childJournalEntitlements?.data?.[0]) {
      return;
    }
    const relationship = childJournal.data || childJournalEntitlements?.data?.[0];
    const includedChildJournal = included.find(
      includedItem =>
        includedItem.id === relationship.id &&
        includedItem.type === relationship.type
    );

    if (includedChildJournal) {
      childJournal.data = includedChildJournal?.attributes;
    }
  });

  const giftCertificates = useMemo(
    () => response?.data ? response?.data?.map(giftCertificateToPlain) : [],
    [response]
  );

  return {
    giftCertificates: giftCertificates.filter(c => c.paid),
    isLoading: enabled && !data && !error,
    isValidating,
    error,
  };
};

export const revalidateDashboard = () => {
  trigger(UNASSIGNED_BOOKS_KEY);
  trigger(UNASSIGNED_ADD_ONS_KEY);
  trigger(MY_CHILD_JOURNALS_KEY);
  trigger(UNASSIGNED_CHILD_JOURNALS_KEY);
  trigger(GIFT_CERTIFICATES_KEY);
};

export const useAssignEntitlement = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const assign = useCallback(async (entitlementId, childJournalId) => {
    setIsSubmitting(true);
    try {
      await patchJournalEntitlement(entitlementId, childJournalId);
      revalidateDashboard();
    } finally {
      setIsSubmitting(false);
    }
  }, []);
  return { assign, isSubmitting };
};

const createdJournalId = response => {
  const [id] = response?.data?.result?.['child-journals'] || [];
  return id || response?.data?.initial?.data?.id || null;
};

// Allocation for the Unassigned tab, split across the two buttons:
//
//   "Confirm >"  → confirmAddOns()      assigns ONLY the selected add-ons. A
//                                       journal is created for the book if it
//                                       doesn't have one yet, because add-ons need
//                                       a journal to attach to. The book
//                                       entitlement is deliberately left
//                                       unassigned, so the book stays in Unassigned.
//   "Set up"     → assignBookToJournal() assigns the book entitlement, reusing the
//                                       journal created by Confirm (or creating one
//                                       if Confirm was never pressed). That's what
//                                       moves the book into My Books.
//
// Nothing is created on page load.
export const useAllocation = () => {
  const [busyBookId, setBusyBookId] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState(null);

  // Lazily create the journal a book will own. Returns the existing id untouched
  // so a book never ends up with two journals.
  const ensureJournal = async (existingJournalId) => {
    if (existingJournalId) return { journalId: existingJournalId, created: false };
    const response = await postChildJournal({
      data: {
        attributes: {
          state: 'unassigned',
        }
      }
    });
    const journalId = createdJournalId(response);
    if (!journalId) throw new Error('The book could not be set up. Please try again.');
    return { journalId, created: true };
  };

  // `pendingByBook`: { key: [addOnEntitlementId] } — the key is a book entitlement
  //   id for a book tile, or a journal id for an unassigned-journal tile.
  // `journalIdByBook`: already-known journal per key, reused where present.
  // `bookEntitlementIds`: the subset of keys that are book entitlements, which get
  //   assigned to their journal alongside the add-ons. Journal-tile keys are
  //   excluded — they have no book entitlement to assign.
  //
  // The book entitlements and add-ons go out together in a single bulk call, which
  // the API applies all-or-nothing — so a failure can't leave a book assigned with
  // its add-ons missing, or vice versa.
  const confirmAddOns = useCallback(async (
    pendingByBook,
    journalIdByBook = {},
    bookEntitlementIds = []
  ) => {
    const bookIds = Object.keys(pendingByBook).filter(id => (pendingByBook[id] || []).length);
    if (!bookIds.length) return { ok: true, journalIds: journalIdByBook };

    setIsConfirming(true);
    setError(null);

    const journalIds = { ...journalIdByBook };
    try {
      for (const bookId of bookIds) {
        // eslint-disable-next-line no-await-in-loop
        const { journalId } = await ensureJournal(journalIds[bookId]);
        journalIds[bookId] = journalId;
      }
    } catch (err) {
      setError(err);
      setIsConfirming(false);
      return { ok: false, journalIds };
    }

    const assignBook = new Set(bookEntitlementIds);
    const assignments = bookIds.flatMap(bookId => {
      const childJournalId = journalIds[bookId];
      const forThisBook = [];
      // The book entitlement itself, when this key is one.
      if (assignBook.has(bookId)) {
        forThisBook.push({ id: bookId, child_journal_id: childJournalId });
      }
      pendingByBook[bookId].forEach(addOnId =>
        forThisBook.push({ id: addOnId, child_journal_id: childJournalId })
      );
      return forThisBook;
    });

    try {
      if (assignments.length === 1) {
        const [only] = assignments;
        await patchJournalEntitlement(only.id, only.child_journal_id);
      } else {
        await bulkPatchJournalEntitlements(assignments);
      }
    } catch (err) {
      setError(err);
      setIsConfirming(false);
      // Journals are kept — they'll be reused on retry and by "Set up".
      return { ok: false, journalIds };
    }

    revalidateDashboard();
    setIsConfirming(false);
    return { ok: true, journalIds };
  }, []);

  // "Set up": assign the book entitlement to its journal.
  const assignBookToJournal = useCallback(async (bookEntitlementId, existingJournalId) => {
    setBusyBookId(bookEntitlementId);
    setError(null);

    let journalId = existingJournalId;
    let createdHere = false;
    try {
      const ensured = await ensureJournal(existingJournalId);
      journalId = ensured.journalId;
      createdHere = ensured.created;
    } catch (err) {
      setError(err);
      setBusyBookId(null);
      return { ok: false };
    }

    try {
      await patchJournalEntitlement(bookEntitlementId, journalId);
    } catch (err) {
      // Only roll back a journal this call created. One that already holds
      // confirmed add-ons must survive, or those add-ons would be orphaned.
      // if (createdHere) {
      //   try {
      //     await destroyChildJournal(journalId);
      //   } catch (cleanupError) {
      //     // Best-effort cleanup; the assignment error is what matters.
      //   }
      // }
      setError(err);
      setBusyBookId(null);
      revalidateDashboard();
      return { ok: false };
    }

    revalidateDashboard();
    setBusyBookId(null);
    return { ok: true, journalId };
  }, []);

  return {
    confirmAddOns,
    assignBookToJournal,
    busyBookId,
    isConfirming,
    isBusy: isConfirming || !!busyBookId,
    error,
  };
};

// JSON:API errors surface a detail/title on the first error object — prefer the
// server's message over generic copy of our own.
export const serverErrorMessage = (error, fallback) => {
  let payload = error?.response?.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      payload = null;
    }
  }
  const first = payload?.errors?.[0];
  return first?.detail || first?.title || fallback;
};
